import type { CompositeScores, Interpretation, PatientInfo, SubtestScores } from '../types';

/**
 * Zapisane diagnozy — czyste funkcje na localStorage (zero Reacta).
 *
 * ⚠️ ŚWIADOMY WYJĄTEK od zasady „dane pacjenta nigdy do localStorage"
 * (CLAUDE.md zasada 6). Decyzja właściciela 2026-06-11: funkcja „wróć do
 * diagnozy" celowo przechowuje zapisy na urządzeniu diagnosty, żeby przeżyły
 * zamknięcie karty/przeglądarki. Ryzyko: komputery współdzielone — dane
 * zostają w przeglądarce; UI udostępnia „Usuń" i „Wyczyść wszystkie".
 * NIE rozszerzaj tego wzorca na inne klucze.
 *
 * Wszystkie operacje są best-effort (try/catch na quota/privacy mode):
 * odczyt przy błędzie zwraca [], zapis przy błędzie zwraca null/false.
 *
 * Konta (logowanie, docs/auth.md): przy AUTH_MODE=session zapisy są PER KONTO —
 * klucz `wiscv-saved-diagnoses-v1:<uid>` (uid = nieprzezroczysty hash z /api/auth/me),
 * więc następna osoba w tej samej przeglądarce nie widzi cudzych diagnoz. Wylogowanie
 * niczego nie kasuje (funkcja „wróć do diagnozy” zostaje), tylko blokuje widok.
 * Przy AUTH_MODE=off (brak kont) działa jak dotąd, na kluczu bez sufiksu.
 */

export const SAVED_DIAGNOSES_STORAGE_KEY = 'wiscv-saved-diagnoses-v1';
/** Znacznik jednorazowej migracji starych (wspólnych) zapisów do pierwszego zalogowanego admina. */
export const SAVED_DIAGNOSES_MIGRATED_KEY = 'wiscv-saved-diagnoses-migrated-v1';

type Owner = { kind: 'shared' } | { kind: 'user'; uid: string } | { kind: 'locked' };
let owner: Owner = { kind: 'shared' };

/** Klucz localStorage dla bieżącego właściciela; null = zablokowane (nikt nie jest zalogowany). */
export const savedDiagnosesKey = (): string | null =>
  owner.kind === 'shared' ? SAVED_DIAGNOSES_STORAGE_KEY : owner.kind === 'user' ? `${SAVED_DIAGNOSES_STORAGE_KEY}:${owner.uid}` : null;

/** AUTH_MODE=off: jedna wspólna lista, jak przed wprowadzeniem kont. */
export const setSharedDiagnoses = (): void => { owner = { kind: 'shared' }; };
/** Tryb session bez zalogowanej osoby (np. po „Wyloguj”): lista pusta, zapis niemożliwy, nic nie jest kasowane. */
export const lockDiagnoses = (): void => { owner = { kind: 'locked' }; };

/**
 * Zalogowana osoba: od teraz lista należy do niej. Pierwszy zalogowany ADMIN przejmuje jednorazowo
 * stare wspólne zapisy (sprzed kont); diagnosta ich nie dostaje (zostają nietknięte do czasu logowania admina).
 */
export const setDiagnosesOwner = (uid: string, role: 'admin' | 'diagnosta'): void => {
  owner = { kind: 'user', uid };
  if (role !== 'admin') return;
  try {
    if (localStorage.getItem(SAVED_DIAGNOSES_MIGRATED_KEY)) return;
    const legacy = localStorage.getItem(SAVED_DIAGNOSES_STORAGE_KEY);
    if (legacy) {
      const mine = readAll();
      const ids = new Set(mine.map((d) => d.id));
      const parsed = JSON.parse(legacy);
      const moved = (Array.isArray(parsed) ? parsed.filter(isSavedDiagnosis) : []).filter((d) => !ids.has(d.id));
      const merged = [...mine, ...moved].sort((a, b) => b.savedAt - a.savedAt).slice(0, MAX_SAVED_DIAGNOSES);
      if (!writeAll(merged)) return; // quota: try again next time, never lose the legacy copy
      localStorage.removeItem(SAVED_DIAGNOSES_STORAGE_KEY);
    }
    localStorage.setItem(SAVED_DIAGNOSES_MIGRATED_KEY, new Date().toISOString());
  } catch {
    // best-effort: the legacy list stays where it was
  }
};

/** Maksymalna liczba zapisów — przy przekroczeniu najstarsze wypadają. */
export const MAX_SAVED_DIAGNOSES = 30;

export interface SavedDiagnosis {
  id: string;
  /** Timestamp (ms) zapisu; aktualizowany też przy update (edycja sekcji). */
  savedAt: number;
  patientInfo: PatientInfo;
  subtestScores: SubtestScores;
  compositeScores: CompositeScores;
  interpretation: Interpretation;
  /** Klucze sekcji interpretacji zredagowanych ręcznie przez diagnostę. */
  editedSections: string[];
}

/** Dane wejściowe zapisu — id i savedAt nadaje store. */
export type SavedDiagnosisInput = Omit<SavedDiagnosis, 'id' | 'savedAt'>;

const isSavedDiagnosis = (value: unknown): value is SavedDiagnosis => {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === 'string' &&
    typeof v.savedAt === 'number' &&
    !!v.interpretation && typeof v.interpretation === 'object' &&
    !!v.patientInfo && typeof v.patientInfo === 'object'
  );
};

const generateId = (): string => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // fall through to the manual generator
  }
  return `diag-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

function readAll(): SavedDiagnosis[] {
  const key = savedDiagnosesKey();
  if (!key) return [];
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isSavedDiagnosis);
  } catch {
    return [];
  }
}

function writeAll(items: SavedDiagnosis[]): boolean {
  const key = savedDiagnosesKey();
  if (!key) return false;
  try {
    localStorage.setItem(key, JSON.stringify(items));
    return true;
  } catch {
    return false;
  }
}

/** Lista zapisów posortowana od najnowszego (savedAt malejąco). */
export const listSavedDiagnoses = (): SavedDiagnosis[] =>
  readAll().sort((a, b) => b.savedAt - a.savedAt);

/**
 * Zapisuje nową diagnozę. Przy przekroczeniu limitu MAX_SAVED_DIAGNOSES
 * najstarsze wpisy wypadają. Zwraca zapisany wpis lub null przy błędzie
 * zapisu (quota/privacy mode).
 */
export const saveDiagnosis = (input: SavedDiagnosisInput): SavedDiagnosis | null => {
  const entry: SavedDiagnosis = {
    ...input,
    id: generateId(),
    savedAt: Date.now(),
  };
  const next = [entry, ...listSavedDiagnoses()].slice(0, MAX_SAVED_DIAGNOSES);
  return writeAll(next) ? entry : null;
};

/**
 * Aktualizuje istniejący wpis (merge pól) i odświeża savedAt.
 * Zwraca zaktualizowany wpis lub null (brak wpisu / błąd zapisu).
 */
export const updateDiagnosis = (
  id: string,
  patch: Partial<SavedDiagnosisInput>
): SavedDiagnosis | null => {
  const items = readAll();
  const index = items.findIndex((item) => item.id === id);
  if (index === -1) return null;
  const updated: SavedDiagnosis = { ...items[index], ...patch, id, savedAt: Date.now() };
  items[index] = updated;
  return writeAll(items) ? updated : null;
};

/** Usuwa wpis. Zwraca true, gdy wpis istniał i zapis się powiódł. */
export const removeDiagnosis = (id: string): boolean => {
  const items = readAll();
  const next = items.filter((item) => item.id !== id);
  if (next.length === items.length) return false;
  return writeAll(next);
};

/** Usuwa wszystkie zapisane diagnozy. */
export const clearDiagnoses = (): void => {
  const key = savedDiagnosesKey();
  if (!key) return;
  try {
    localStorage.removeItem(key);
  } catch {
    // best-effort
  }
};
